import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PencilSquare, Trash, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import CommunicationIcon from '../img/communication.gif';
import CommunicationGuideModal from '../Components/CommunicationGuideModal.jsx';
import { communicationAPI } from '../services/api';
import '../CSS/Communication.css';

function Communication() {
  const [announcementText, setAnnouncementText] = useState('');
  const [announcements, setAnnouncements]       = useState([]);
  const [isLoading, setIsLoading]               = useState(false);
  const [showPostModal, setShowPostModal]        = useState(false);
  const [editIndex, setEditIndex]               = useState(null);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [deleteIndex, setDeleteIndex]           = useState(null);
  const [toast, setToast]                       = useState({ show: false, message: '', variant: 'success' });

  const user = useSelector(state => state.auth.user);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // Auto-dismiss toast after 3 s
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    return () => clearTimeout(t);
  }, [toast.show]);

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
  };

  const fetchAnnouncements = async () => {
    setIsLoading(true);
    try {
      const response = await communicationAPI.getAnnouncements();
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      showToast('Failed to fetch announcements', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAnnouncement = async () => {
    if (!announcementText.trim()) {
      showToast('Please enter an announcement', 'danger');
      return;
    }

    const payload = {
      text: announcementText.trim(),
      user: user.name,
      role: user.role,
    };

    try {
      if (editIndex !== null) {
        await communicationAPI.updateAnnouncement(announcements[editIndex].id, payload);
      } else {
        await communicationAPI.createAnnouncement(payload);
      }
      await fetchAnnouncements();
      setAnnouncementText('');
      setEditIndex(null);
      setShowPostModal(false);
      showToast(editIndex !== null ? 'Announcement updated successfully!' : 'Announcement posted successfully!');
    } catch (error) {
      console.error('Error adding/updating announcement:', error);
      showToast(editIndex !== null ? 'Failed to update announcement' : 'Failed to post announcement', 'danger');
    }
  };

  const handleDeleteAnnouncement = async () => {
    try {
      await communicationAPI.deleteAnnouncement(announcements[deleteIndex].id);
      await fetchAnnouncements();
      setShowDeleteModal(false);
      setDeleteIndex(null);
      showToast('Announcement deleted successfully!');
    } catch (error) {
      console.error('Error deleting announcement:', error);
      showToast('Failed to delete announcement', 'danger');
    }
  };

  const handleEditClick = (index, text) => {
    setAnnouncementText(text);
    setEditIndex(index);
    setShowPostModal(true);
  };

  const handleClosePostModal = () => {
    setShowPostModal(false);
    setAnnouncementText('');
    setEditIndex(null);
  };

  return (
    <div className="cmm-page">

      {/* ── Toast ── */}
      <div className="cmm-toast-region" aria-live="polite" aria-atomic="true">
        <div
          className={`cmm-toast cmm-toast-${toast.variant} ${toast.show ? 'cmm-toast-show' : ''}`}
          role="alert"
        >
          <div className="cmm-toast-header">
            <span className="cmm-toast-title">
              {toast.variant === 'success' ? 'Success' : 'Error'}
            </span>
            <button
              className="cmm-toast-close"
              onClick={() => setToast(p => ({ ...p, show: false }))}
              aria-label="Close"
            >×</button>
          </div>
          <div className="cmm-toast-body">
            {toast.variant === 'success'
              ? <CheckCircleFill className="cmm-toast-icon" />
              : <ExclamationTriangleFill className="cmm-toast-icon" />}
            {toast.message}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="cmm-inner-container">

        {/* Page heading */}
        <div className="row my-4">
          <div className="col-12">
            <h3>
              Communication
              <img className="cmm-animated-icon" src={CommunicationIcon} alt="Communication icon" />
            </h3>
          </div>
        </div>

        {/* Announcements section */}
        <section className="cmm-section">
          <h4 className="cmm-section-label">Announcements</h4>
          <hr className="cmm-divider" />

          {/* Post button */}
          <div className="cmm-toolbar">
            <button
              className="cmm-btn cmm-btn-success"
              onClick={() => { setEditIndex(null); setAnnouncementText(''); setShowPostModal(true); }}
            >
              Post Announcement
            </button>
          </div>

          {/* Announcement list */}
          <div className="cmm-list">
            {isLoading ? (
              <div className="cmm-loader-wrap">
                <div className="cmm-loader-ring" />
                <span className="cmm-loader-text">Loading Announcements</span>
              </div>
            ) : announcements.length === 0 ? (
              <div className="cmm-empty-state">No announcements yet.</div>
            ) : (
              announcements.map((announcement, index) => (
                <div className="cmm-card" key={announcement.id}>
                  <p className="cmm-card-text">{announcement.text}</p>
                  <div className="cmm-card-meta">
                    <span className="cmm-card-timestamp">{announcement.timestamp}</span>
                    <span className="cmm-card-user">
                      Posted by: {announcement.user} ({announcement.role})
                    </span>
                  </div>
                  <div className="cmm-card-actions">
                    <button
                      className="cmm-action-btn cmm-action-edit"
                      onClick={() => handleEditClick(index, announcement.text)}
                      title="Edit announcement"
                    >
                      <PencilSquare />
                    </button>
                    <button
                      className="cmm-action-btn cmm-action-delete"
                      onClick={() => { setDeleteIndex(index); setShowDeleteModal(true); }}
                      title="Delete announcement"
                    >
                      <Trash />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* ── Post / Edit Modal ── */}
        {showPostModal && (
          <div className="cmm-modal-overlay" onClick={handleClosePostModal}>
            <div className="cmm-modal-content" onClick={e => e.stopPropagation()}>
              <div className="cmm-modal-header cmm-modal-header--info">
                <h5 className="cmm-modal-title">
                  {editIndex !== null ? 'Edit Announcement' : 'Post Announcement'}
                </h5>
                <button className="cmm-modal-close" onClick={handleClosePostModal} aria-label="Close">×</button>
              </div>
              <div className="cmm-modal-body">
                <div className="cmm-form-field">
                  <textarea
                    className="cmm-textarea"
                    value={announcementText}
                    onChange={(e) => setAnnouncementText(e.target.value)}
                    placeholder="Enter your announcement (you can use emojis too!)"
                    rows={5}
                  />
                </div>
              </div>
              <div className="cmm-modal-footer">
                <button className="cmm-modal-btn cmm-modal-btn--secondary" onClick={handleClosePostModal}>
                  Close
                </button>
                <button className="cmm-modal-btn cmm-modal-btn--primary" onClick={handleAddAnnouncement}>
                  {editIndex !== null ? 'Save Changes' : 'Post'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete Confirmation Modal ── */}
        {showDeleteModal && (
          <div className="cmm-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="cmm-modal-content" onClick={e => e.stopPropagation()}>
              <div className="cmm-modal-header cmm-modal-header--danger">
                <h5 className="cmm-modal-title">Delete Announcement</h5>
                <button className="cmm-modal-close" onClick={() => setShowDeleteModal(false)} aria-label="Close">×</button>
              </div>
              <div className="cmm-modal-body">
                <div className="cmm-delete-alert">
                  Are you sure you want to delete this announcement?
                </div>
              </div>
              <div className="cmm-modal-footer cmm-modal-footer--dark">
                <button className="cmm-modal-btn cmm-modal-btn--secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="cmm-modal-btn cmm-modal-btn--danger" onClick={handleDeleteAnnouncement}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
       <CommunicationGuideModal />
    </div>
  );
}

export default Communication;